import { Link, Outlet, useNavigate } from 'react-router'
import type { JSX, ReactNode } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { ThemeToggle } from '@/components/ThemeToggle'
import { Lockup } from '@/shell/Lockup'
import { Button } from '@/ui/Button'
import { ErrorState, FullPageLoading } from '@/ui/states'
import { useOnboarding, useWizard } from './context'
import { metaFor, pathFor, timeRemaining } from './steps'
import type { StepKey } from './steps'

/**
 * The frame every wizard screen sits in — S-D2's chrome, without the review rail.
 *
 * Its own layout rather than `AppShell`, because a tenant part-way through setup
 * has nothing for a sidebar to describe: no keywords to link to, no dashboard
 * worth showing, and a module switcher between two empty modules. S-D2 draws it
 * as a single centred column and that is what this is.
 *
 * The step rail is **navigable**. Every dot is a link, including to steps that
 * are already done, because S-D2's *Back* button exists to change an answer —
 * and a rail that only moved forward would make the answer already given
 * unreachable without starting again.
 */
export function WizardLayout(): JSX.Element {
  const { status, failure, reload } = useOnboarding()

  if (status === 'loading') return <FullPageLoading label="Loading your setup" />
  if (status === 'error' && failure) {
    return (
      <div className="app-full-state">
        <ErrorState failure={failure} title="Could not load your setup" onRetry={reload} />
      </div>
    )
  }

  return (
    <div className="app-wizard">
      <div className="app-wizard__bar">
        <Link className="app-public__lockup" to="/onboarding">
          <Lockup />
        </Link>
        <span className="app-spacer" />
        <ThemeToggle />
      </div>
      <Outlet />
    </div>
  )
}

/**
 * The progress bar and the step rail, above every step's card.
 *
 * Both are derived from the server's `progress` and `steps`, never counted
 * here — see `onboarding/steps.ts` for why the SPA holds no second ordering.
 */
export function WizardChrome({ current }: { current: StepKey }): JSX.Element {
  const state = useWizard()
  const position = state.steps.findIndex((row) => row.step === current) + 1

  return (
    <>
      <div className="app-wiz__progress">
        <div className="app-wiz__progress-label">
          <span>
            Step {position} of {state.steps.length}
          </span>
          <span>{timeRemaining(state)}</span>
        </div>
        <div className="ar-meter">
          <div className="ar-meter__fill" style={{ width: `${String(state.progress.percent)}%` }} />
        </div>
      </div>

      <ol className="app-steps">
        {state.steps.map((row, index) => {
          const meta = metaFor(row.step)
          if (!meta) return null
          const isCurrent = row.step === current
          return (
            <li key={row.step}>
              <Link
                className="app-step"
                to={pathFor(row.step)}
                data-state={row.status}
                aria-current={isCurrent ? 'step' : undefined}
              >
                {/* Each row's own position — `position` above belongs to the
                 *current* step, and using it here numbered every dot 1. */}
                <span className="app-step__dot" aria-hidden="true">
                  {row.status === 'done' ? '✓' : row.status === 'failed' ? '!' : index + 1}
                </span>
                {meta.short}
                <span className="ar-visually-hidden">
                  {isCurrent ? ' (current step)' : ` (${row.status})`}
                </span>
              </Link>
            </li>
          )
        })}
      </ol>
    </>
  )
}

/**
 * The footer every step shares: Back, Skip, and the step's own primary action.
 *
 * *Skip* is absent for step 1, which the server refuses to skip anyway — the
 * button is hidden because offering an action that 409s is worse than not
 * offering it, and `optional` comes from the server so the two cannot disagree.
 */
export function WizardFooter({
  current,
  children,
}: {
  current: StepKey
  children: ReactNode
}): JSX.Element {
  const state = useWizard()
  const { adopt } = useOnboarding()
  const navigate = useNavigate()

  const index = state.steps.findIndex((row) => row.step === current)
  const previous = index > 0 ? state.steps[index - 1] : undefined
  const optional = state.steps[index]?.optional ?? true

  const skip = useMutation((step: string) =>
    call(() => api.POST('/api/v1/onboarding/steps/{step}/skip', { params: { path: { step } } })),
  )
  const back = useMutation((step: string) =>
    call(() => api.POST('/api/v1/onboarding/navigate', { body: { step } })),
  )

  async function onBack(): Promise<void> {
    if (!previous) return
    // The cursor moves server-side too, so closing the tab on the previous step
    // resumes there rather than on the step that was left behind.
    const result = await back.run(previous.step)
    if (result.ok) adopt(result.data)
    void navigate(pathFor(previous.step))
  }

  async function onSkip(): Promise<void> {
    const result = await skip.run(current)
    if (result.ok) {
      adopt(result.data)
      void navigate(pathFor(result.data.current_step))
    }
  }

  return (
    <div className="ar-card__footer">
      {previous ? (
        <Button
          busy={back.pending}
          busyLabel="Going back"
          onClick={() => {
            void onBack()
          }}
        >
          Back
        </Button>
      ) : null}
      <span className="ar-card__spacer" />
      {optional ? (
        <Button
          tone="quiet"
          busy={skip.pending}
          busyLabel="Skipping"
          onClick={() => {
            void onSkip()
          }}
        >
          Skip for now
        </Button>
      ) : null}
      {children}
    </div>
  )
}
